import { moneyCents } from './timedFeeDefaults';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  TimedEventExpenseSummary,
  TimedEventExpenseWrite,
  TimedEventExpenseReceipt,
} from '@pokerroom/types';
import { ActionKey } from '@pokerroom/types';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';
import { api } from '../../lib/api';

const FIELD = 'block w-full min-h-11 border border-base-300 bg-base-100 px-3 py-2';
const BUTTON = 'min-h-11 border border-base-300 px-4 py-2 disabled:opacity-50';
export default function TimedEventExpenses({
  tournamentId,
  finished,
}: {
  tournamentId: string;
  finished: boolean;
}) {
  const { t } = useTranslation('tournament');
  const qc = useQueryClient();
  const canRead = useAuthStore(
    (s) => !!s.user?.isSystemUser || !!s.allowedActions?.includes(ActionKey.ACCOUNTING_READ),
  );
  const canWrite = useAuthStore(
    (s) => !!s.user?.isSystemUser || !!s.allowedActions?.includes(ActionKey.ACCOUNTING_EDIT),
  );
  const scope = useAuthStore((s) => `${s.user?.id}:${sessionVenueKey(s.accessToken)}`);
  const key = ['timed-event-expenses', scope, tournamentId, finished];
  const storageKey = `timed-expense-intent:${scope}:${tournamentId}`;
  const [intent, setIntent] = useState<TimedEventExpenseWrite | null>(() => {
    try {
      return JSON.parse(
        sessionStorage.getItem(storageKey) ?? 'null',
      ) as TimedEventExpenseWrite | null;
    } catch {
      return null;
    }
  });
  const [amount, setAmount] = useState('');
  const [locationId, setLocationId] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [note, setNote] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [message, setMessage] = useState('');
  const summary = useQuery({
    queryKey: key,
    queryFn: () =>
      api.get<TimedEventExpenseSummary>(`/accounting/timed-events/${tournamentId}/expenses`),
    enabled: canRead,
  });
  const base = `/accounting/timed-events/${tournamentId}/expenses`;
  const completed = async () => {
    sessionStorage.removeItem(storageKey);
    setIntent(null);
    setMessage(t('eventExpenses.saved'));
    setAmount('');
    setNote('');
    setEvidenceRef('');
    setTransactionId('');
    await qc.invalidateQueries({ queryKey: key });
    await qc.invalidateQueries({ queryKey: ['cash-workflow'] });
  };
  const describeError = (error: Error) => {
    if (isAxiosError(error)) {
      if (!error.response) return t('eventExpenses.network');
      if (error.response.status === 404) return t('eventExpenses.notFound');
      const code = error.response.data?.code ?? error.response.data?.errorCode;
      return t(`eventExpenses.errors.${code}`, { defaultValue: t('eventExpenses.failed') });
    }
    return t('eventExpenses.failed');
  };
  const write = useMutation({
    mutationFn: (body: TimedEventExpenseWrite) => api.post<TimedEventExpenseReceipt>(base, body),
    onSuccess: completed,
    onError: (e: Error) => {
      setMessage(describeError(e));
      if (
        isAxiosError(e) &&
        [400, 409].includes(e.response?.status ?? 0) &&
        (e.response?.data?.code ?? e.response?.data?.errorCode) !== 'TIMED_EXPENSE_KEY_REUSED'
      ) {
        sessionStorage.removeItem(storageKey);
        setIntent(null);
        void summary.refetch();
      }
    },
  });
  const lookup = useMutation({
    mutationFn: () =>
      api.get<TimedEventExpenseReceipt>(`${base}/operations/${intent!.operationKey}`),
    onSuccess: completed,
    onError: (e: Error) => setMessage(describeError(e)),
  });
  const data = summary.data;
  const busy = write.isPending || lookup.isPending;
  const selected = data?.locations.find((l) => l.id === locationId);
  const amountCents = moneyCents(amount);
  const spendable = moneyCents(selected?.spendable ?? '0') ?? 0n;
  const remainingFee = moneyCents(data?.venueFeeNet ?? '0') ?? 0n;
  const zeroReason =
    remainingFee <= 0n ? 'eventExpenses.feeExhausted' : 'eventExpenses.cashExhausted';
  const createAllowed =
    !!selected && amountCents !== null && amountCents > 0n && amountCents <= spendable;
  const stale = summary.isError || summary.isFetching;
  const start = (action: TimedEventExpenseWrite['action'], expenseId?: string) => {
    if (!data || !data.writable || stale || !canWrite || intent || busy || !note.trim()) return;
    if (action === 'CREATE' && !createAllowed) return;
    if (
      !window.confirm(
        t(action === 'REFUND' ? 'eventExpenses.refundConfirm' : 'eventExpenses.confirm', {
          amount:
            action === 'CREATE'
              ? amount
              : action === 'LINK'
                ? data.linkable.find((x) => x.id === transactionId)?.amount
                : data.expenses.find((x) => x.id === expenseId)?.amount,
        }),
      )
    )
      return;
    const body: TimedEventExpenseWrite = {
      operationKey: crypto.randomUUID(),
      expectedVersion: data.version,
      action,
      note: note.trim(),
      ...(action === 'CREATE'
        ? { amount, locationId, evidenceRef: evidenceRef.trim() }
        : action === 'LINK'
          ? { transactionId }
          : {
              expenseId,
              ...(action === 'REFUND'
                ? { cashReturned: true, evidenceRef: evidenceRef.trim() }
                : {}),
            }),
    };
    // Persist the exact request before sending. Unknown outcomes retain the same operation key.
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(body));
    } catch {
      setMessage(t('eventExpenses.storage'));
      return;
    }
    setIntent(body);
    setMessage('');
    write.mutate(body);
  };
  if (!canRead || (!finished && !data?.expenses.length)) return null;
  return (
    <section
      className="space-y-3 border border-base-300 p-4 print:hidden"
      aria-label={t('eventExpenses.title')}
    >
      <h3 className="font-semibold">{t('eventExpenses.title')}</h3>
      {summary.isLoading && <p role="status">{t('eventExpenses.loading')}</p>}
      {summary.error && (
        <p role="alert">
          {t('eventExpenses.loadFailed')}
          <button type="button" className={BUTTON} onClick={() => summary.refetch()}>
            {t('eventExpenses.refresh')}
          </button>
        </p>
      )}
      {data && (
        <>
          {!data.finished && <p role="status">{t('eventExpenses.reopened')}</p>}
          <dl className="grid grid-cols-2 gap-2 tabular-nums sm:grid-cols-4">
            {(
              [
                'venueFeeGross',
                'activityFee1',
                'activityFee2',
                'zPoints',
                'expenseTotal',
                'venueFeeNet',
              ] as const
            ).map((field) => (
              <div key={field}>
                <dt>{t(`eventExpenses.${field}`)}</dt>
                <dd>
                  {data.finished || field === 'expenseTotal'
                    ? data[field]
                    : t('eventExpenses.pending')}
                </dd>
              </div>
            ))}
          </dl>
          <p className="text-sm text-base-content/70">{t('eventExpenses.separate')}</p>
          {data.expenses.map((e) => (
            <div key={e.id} className="border border-base-300 p-3">
              <p>
                {e.amount} · {t(`eventExpenses.${e.locationKind}`)} · {e.note} · {e.evidenceRef}
              </p>
              <p>
                {t(
                  e.refunded
                    ? 'eventExpenses.refunded'
                    : e.linked
                      ? 'eventExpenses.linked'
                      : 'eventExpenses.unlinked',
                )}
              </p>
              {!e.refunded && e.locationKind === 'EVENT_BASKET' && (
                <p className="text-sm text-base-content/70">{t('eventExpenses.basketNoUnlink')}</p>
              )}
              {canWrite && data.writable && !e.refunded && (
                <div className="flex flex-wrap gap-2">
                  {e.linked && e.locationKind !== 'EVENT_BASKET' && (
                    <button
                      type="button"
                      className={BUTTON}
                      disabled={busy || !!intent || !note.trim()}
                      onClick={() => start('UNLINK', e.id)}
                    >
                      {t('eventExpenses.unlink')}
                    </button>
                  )}
                  {
                    <button
                      type="button"
                      className={BUTTON}
                      disabled={busy || !!intent || !note.trim() || !evidenceRef.trim()}
                      onClick={() => start('REFUND', e.id)}
                    >
                      {t('eventExpenses.refund')}
                    </button>
                  }
                </div>
              )}
            </div>
          ))}
          {!data.writable && <p role="status">{t('eventExpenses.readonly')}</p>}
          {canWrite && data.writable && !intent && (
            <div className="space-y-3">
              {data.locations.every((l) => (moneyCents(l.spendable) ?? 0n) <= 0n) && (
                <p role="status">{t(zeroReason)}</p>
              )}
              <label className="block">
                {t('eventExpenses.amount')}
                <input
                  className={FIELD}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </label>
              <label className="block">
                {t('eventExpenses.source')}
                <select
                  className={FIELD}
                  aria-label={t('eventExpenses.source')}
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                >
                  <option value="">{t('eventExpenses.choose')}</option>
                  {data.locations.map((l) => (
                    <option
                      key={l.id}
                      value={l.id}
                      disabled={(moneyCents(l.spendable) ?? 0n) <= 0n}
                    >
                      {t(`eventExpenses.${l.kind}`)} ·{' '}
                      {t('eventExpenses.spendable', { amount: l.spendable })}
                      {(moneyCents(l.spendable) ?? 0n) <= 0n ? ` · ${t(zeroReason)}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              {selected && (
                <p role="status">
                  {t('eventExpenses.sourceLimits', {
                    remaining: data.venueFeeNet,
                    balance: selected.balance,
                  })}
                </p>
              )}
              {selected &&
                spendable > 0n &&
                (moneyCents(selected.balance) ?? 0n) < remainingFee && (
                  <p role="status">
                    {t('eventExpenses.cashLimited', { amount: selected.spendable })}
                  </p>
                )}
              {selected && spendable === 0n && <p role="status">{t(zeroReason)}</p>}
              {selected && amountCents !== null && amountCents > spendable && (
                <p role="alert">
                  {t('eventExpenses.overSpendable', { amount: selected.spendable })}
                </p>
              )}
              <label className="block">
                {t('eventExpenses.evidence')}
                <input
                  className={FIELD}
                  maxLength={255}
                  value={evidenceRef}
                  onChange={(e) => setEvidenceRef(e.target.value)}
                />
              </label>
              <label className="block">
                {t('eventExpenses.note')}
                <input
                  className={FIELD}
                  maxLength={255}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <button
                type="button"
                className={BUTTON}
                disabled={busy || stale || !createAllowed || !note.trim() || !evidenceRef.trim()}
                onClick={() => start('CREATE')}
              >
                {t('eventExpenses.create')}
              </button>
              <label className="block">
                {t('eventExpenses.existing')}
                <select
                  className={FIELD}
                  aria-label={t('eventExpenses.existing')}
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                >
                  <option value="">{t('eventExpenses.choose')}</option>
                  {data.linkable.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.amount} · {x.description}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className={BUTTON}
                disabled={busy || stale || !transactionId || !note.trim()}
                onClick={() => start('LINK')}
              >
                {t('eventExpenses.link')}
              </button>
            </div>
          )}
        </>
      )}
      {intent && (
        <div className="space-y-2 border border-warning p-3">
          <p role="status">
            {t('eventExpenses.unknown')}{' '}
            <span className="break-all font-mono">{intent.operationKey}</span>
          </p>
          <button type="button" className={BUTTON} disabled={busy} onClick={() => lookup.mutate()}>
            {t('eventExpenses.lookup')}
          </button>
          {canWrite && (
            <button
              type="button"
              className={BUTTON}
              disabled={busy}
              onClick={() => write.mutate(intent)}
            >
              {t('eventExpenses.retry')}
            </button>
          )}
        </div>
      )}
      {message && <p role="status">{message}</p>}
    </section>
  );
}
